/**
 * VARIANT IMAGE FILTER
 * Gabriela Artigas — Custom Prestige Theme Enhancement
 *
 * Filters the Prestige product gallery to show only images assigned to the currently selected
 * variant via the custom.variant_images metafield (namespace: custom, key: variant_images,
 * type: list.file_reference). Images are displayed in the order they appear in the metafield.
 *
 * Data is surfaced by snippets/variant-image-filter-data.liquid as a JSON script block.
 *
 * Integration with Prestige:
 * - Listens for the `variant:change` CustomEvent fired on the product form (the same event
 *   Prestige's ProductGallery component uses internally).
 * - Uses { capture: true } so this handler fires BEFORE Prestige's native handler. This ensures
 *   the `filtered-indexes` attribute is up to date when Prestige's handler reads it to calculate
 *   its scroll position.
 * - Calls carousel.filter() — Prestige's own public method on the <scroll-carousel> element —
 *   which toggles `hidden` on media cells AND fires `carousel:filter` to sync thumbnails and dots.
 *   This preserves all native lightbox, zoom, and swipe behavior.
 * - Reorders carousel cells, thumbnails, and dot controls to match metafield order before filtering.
 * - Falls back to showing all images (empty filter) if a variant has no metafield data assigned.
 *
 * Does not modify theme.js or any other core theme file.
 */

(function () {
  'use strict';

  function initVariantImageFilter(dataScript) {
    var data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }

    var formId = data.formId;
    var variantMediaMap = data.variantMediaMap || {};
    var initialVariantId = String(data.initialVariantId);

    var form = document.getElementById(formId);
    var productGallery = document.querySelector('product-gallery[form="' + formId + '"]');

    if (!form || !productGallery) {
      return;
    }

    // Normalize all media IDs to strings for consistent comparison.
    var normalizedMap = {};
    Object.keys(variantMediaMap).forEach(function (variantId) {
      normalizedMap[String(variantId)] = (variantMediaMap[variantId] || []).map(String);
    });

    /**
     * Reorder an element's direct children so that `orderedFirst` nodes appear before `rest`.
     * Only moves nodes whose parentNode is `container` (safe no-op otherwise).
     */
    function reorderChildren(container, orderedFirst, rest) {
      orderedFirst.concat(rest).forEach(function (node) {
        if (node && node.parentNode === container) {
          container.appendChild(node);
        }
      });
    }

    /**
     * Given a list of all items and the target media ID order, split into
     * [targetItems (in metafield order), otherItems].
     * Items are matched to targetMediaIds via their own `data-media-id` attribute.
     *
     * @param {Element[]} allItems      - all sibling elements to split
     * @param {string[]}  targetIds     - media IDs in desired display order
     * @returns {{ targeted: Element[], others: Element[] }}
     */
    function splitByMediaId(allItems, targetIds) {
      var byId = {};
      allItems.forEach(function (item) {
        var id = item.getAttribute('data-media-id');
        if (id) byId[id] = item;
      });

      var targeted = targetIds
        .map(function (id) { return byId[id] || null; })
        .filter(Boolean);

      var others = allItems.filter(function (item) {
        var id = item.getAttribute('data-media-id');
        return !id || targetIds.indexOf(id) === -1;
      });

      return { targeted: targeted, others: others };
    }

    /**
     * Apply gallery filtering and reordering for the given variant ID.
     *
     * @param {string}  variantId
     * @param {boolean} deferScroll — when true, scroll correction runs in the next rAF so it
     *                                fires after Prestige's own bubble-phase onVariantChange_fn.
     */
    function applyFilter(variantId, deferScroll) {
      var carousel = productGallery.carousel;
      if (!carousel || typeof carousel.filter !== 'function') {
        return;
      }

      var targetMediaIds = normalizedMap[String(variantId)];

      if (!targetMediaIds || targetMediaIds.length === 0) {
        // No metafield data — show everything in current DOM order.
        carousel.filter([]);
        productGallery.setAttribute('filtered-indexes', '[]');
        return;
      }

      // -----------------------------------------------------------------------
      // 1. Reorder carousel cells to match metafield order.
      //    carousel.allCells / carousel.children are the direct media cell divs.
      // -----------------------------------------------------------------------
      var allCells = Array.from(carousel.allCells || carousel.children);
      var cellSplit = splitByMediaId(allCells, targetMediaIds);
      reorderChildren(carousel, cellSplit.targeted, cellSplit.others);

      // -----------------------------------------------------------------------
      // 2. Reorder thumbnails the same way.
      //    Thumbnails have data-media-id so we can match them directly by ID,
      //    independent of positional assumptions.
      // -----------------------------------------------------------------------
      var thumbnailNav = productGallery.querySelector('product-gallery-navigation');
      if (thumbnailNav) {
        var allThumbs = Array.from(thumbnailNav.querySelectorAll('[data-media-id]'));
        var thumbSplit = splitByMediaId(allThumbs, targetMediaIds);
        reorderChildren(thumbnailNav, thumbSplit.targeted, thumbSplit.others);
      }

      // -----------------------------------------------------------------------
      // 3. Reorder dot controls (best-effort).
      //    Dots have no data-media-id. We match them to cells by position in
      //    the allCells snapshot taken BEFORE the carousel reorder (so indices
      //    still reflect the current DOM order that dots mirror).
      // -----------------------------------------------------------------------
      var dotNav = productGallery.querySelector('carousel-navigation');
      if (dotNav) {
        var dotButtons = Array.from(dotNav.querySelectorAll('button'));
        if (dotButtons.length === allCells.length) {
          // Build a parallel map: allCells[i] → dotButtons[i]
          var dotForCell = {};
          allCells.forEach(function (cell, i) {
            dotForCell[i] = dotButtons[i];
          });

          var targetDots = cellSplit.targeted.map(function (cell) {
            return dotForCell[allCells.indexOf(cell)] || null;
          }).filter(Boolean);

          var otherDots = cellSplit.others.map(function (cell) {
            return dotForCell[allCells.indexOf(cell)] || null;
          }).filter(Boolean);

          reorderChildren(dotNav, targetDots, otherDots);
        }
      }

      // -----------------------------------------------------------------------
      // 4. Apply the filter on the now-reordered cells.
      //    Target cells are at indices 0..N-1; everything after is hidden.
      // -----------------------------------------------------------------------
      var reorderedCells = Array.from(carousel.allCells || carousel.children);
      var indexesToHide = [];
      reorderedCells.forEach(function (cell, index) {
        var id = cell.getAttribute('data-media-id');
        if (id && targetMediaIds.indexOf(id) === -1) {
          indexesToHide.push(index);
        }
      });

      carousel.filter(indexesToHide);

      // Fix thumbnail visibility after carousel:filter fires.
      //
      // carousel.filter() dispatches "carousel:filter" synchronously. Prestige's
      // CarouselNavigation handler responds by iterating its _allItems cache
      // (buttons captured in connectedCallback, in the ORIGINAL DOM order) and
      // toggling hidden by index using our filteredIndexes. Because filteredIndexes
      // reflects the REORDERED carousel positions, _allItems-based indexing hides
      // the wrong thumbnails. Correct this immediately after filter() returns.
      if (thumbnailNav) {
        Array.from(thumbnailNav.querySelectorAll('[data-media-id]')).forEach(function (thumb) {
          var id = thumb.getAttribute('data-media-id');
          thumb.toggleAttribute('hidden', targetMediaIds.indexOf(id) === -1);
        });
      }

      // Keep <product-gallery filtered-indexes> in sync so Prestige's internal
      // scroll calculation (onVariantChange_fn) uses the correct offsets.
      productGallery.setAttribute(
        'filtered-indexes',
        JSON.stringify(indexesToHide.map(function (i) { return i + 1; }))
      );

      // -----------------------------------------------------------------------
      // 5. Scroll to position 0 (first image in metafield order).
      //    When called from a variant:change event (deferScroll=true), we wait
      //    one rAF so this runs after Prestige's own scroll attempt, overriding
      //    it. For initial page load (deferScroll=false) we run immediately.
      // -----------------------------------------------------------------------
      function scrollToFirst() {
        if (carousel.isScrollable) {
          carousel.select(0, { instant: true });
        }
      }

      if (deferScroll) {
        requestAnimationFrame(scrollToFirst);
      } else {
        scrollToFirst();
      }
    }

    // Apply filter for the initially selected variant on page load.
    requestAnimationFrame(function () {
      applyFilter(initialVariantId, false);
    });

    // React to variant changes, running before Prestige's bubble-phase handler.
    form.addEventListener('variant:change', function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;
      applyFilter(String(variant.id), true);
    }, { capture: true });
  }

  function bootstrap() {
    var dataScripts = document.querySelectorAll('script[id^="variant-image-filter-data-"]');
    dataScripts.forEach(initVariantImageFilter);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
